"use client";

import React from "react";
import { Search, FileText, ShieldCheck, Key, Building2, Ban } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const trustPods = [{
  title: "Ready-to-Deploy RAG",
  description: "Launch a business-ready retrieval layer without building your own chunking, indexing, retrieval, and answer pipeline from scratch.",
  icon: Search
}, {
  title: "Citation-Based Answers",
  description: "Every grounded answer is backed by source citations, so teams can verify where the information came from.",
  icon: FileText
}, {
  title: "Approved Content Only",
  description: "The answering engine is restricted to approved knowledge only. Draft and review content stays inaccessible until explicitly approved.",
  icon: ShieldCheck
}, {
  title: "BYO-Key Cost Control",
  description: "Customers bring their own AI and OCR keys, so variable usage stays with the provider and platform pricing remains predictable.",
  icon: Key
}, {
  title: "White-Label Ready",
  description: "Agencies and resellers can deploy branded client environments without building a separate multi-tenant RAG product themselves.",
  icon: Building2
}, {
  title: "Refusal Over Guessing",
  description: "If approved evidence is insufficient, EloRAG refuses instead of producing unsupported answers.",
  icon: Ban
}];
export default function VerifiedTrustSignals() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="verified-trust-signals" className="py-16 lg:py-20 bg-background overflow-hidden" ref={ref}>
      <div className="container mx-auto px-6 max-w-7xl">
        <div className={cn("mb-10 max-w-3xl transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4 max-w-2xl">
            Why teams choose EloRAG instead of building RAG in-house
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            EloRAG gives businesses and agencies a ready-to-deploy RAG platform with grounded answers,
            citations, approved-only access, and predictable deployment economics — without having to
            assemble and maintain the full stack internally.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trustPods.map((pod, i) => {
          const Icon = pod.icon;
          return <div key={i} data-index={i} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
            transitionDelay: `${i * 90}ms`
          }}>
                <Card className="h-full min-h-[230px] p-6 flex flex-col gap-4 border-border rounded-xl bg-card hover:border-primary/30 transition-colors duration-300 shadow-none">
                  <div className="p-3 bg-primary/10 w-fit rounded-lg">
                    <Icon className="size-6 text-primary" strokeWidth={2} />
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-[18px] font-bold text-foreground leading-tight">
                      {pod.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pod.description}
                    </p>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}